import { useEffect, useSyncExternalStore } from 'react'

export type ThemePref = 'system' | 'light' | 'dark'
const KEY = 'nebueye.theme'
const media = () => matchMedia('(prefers-color-scheme: dark)')

function read(): ThemePref {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'light' || v === 'dark' ? v : 'system'
  } catch {
    return 'system'
  }
}

const listeners = new Set<() => void>()
let current = read()

function apply(pref: ThemePref) {
  const dark = pref === 'dark' || (pref === 'system' && media().matches)
  document.documentElement.classList.toggle('dark', dark)
}

export function setTheme(pref: ThemePref) {
  current = pref
  try {
    localStorage.setItem(KEY, pref)
  } catch {
    /* private mode: preference lasts for this tab only */
  }
  apply(pref)
  listeners.forEach((l) => l())
}

export function useTheme() {
  const pref = useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => current,
  )
  useEffect(() => {
    const m = media()
    const onChange = () => current === 'system' && apply('system')
    m.addEventListener('change', onChange)
    return () => m.removeEventListener('change', onChange)
  }, [])
  return [pref, setTheme] as const
}
